import React from "react";
import { motion } from "framer-motion";
import { Plus, ArrowUpRight, Sparkles } from "lucide-react";
import PhoneAnchor from "./PhoneAnchor";
import UNSPLASH from "../../unsplashImages";

const EXAMPLE_OFFERINGS = [
  {
    title: "Sunset Catamaran Charter",
    price: "$220",
    tag: "Experience",
    img: UNSPLASH.services.arrivalEvent,
  },
  {
    title: "Beach Cruiser Rentals",
    price: "$45 / day",
    tag: "Rental",
    img: UNSPLASH.services.airportTransfer,
  },
  {
    title: "Arrival Champagne Setup",
    price: "$85",
    tag: "Signature",
    img: UNSPLASH.services.arrivalEvent,
  },
];

export default function StageMarketplace({
  onCreate,
  customServices = [],
  hostLogo,
  previewToggles,
  compactHeight = false,
}) {
  const enabledServices = React.useMemo(() => {
    const set = new Set(["grocery-delivery"]);
    Object.entries(previewToggles || {}).forEach(([k, on]) => {
      if (on) set.add(k.replace(/_/g, "-"));
    });
    return set;
  }, [previewToggles]);

  return (
    <div
      className={`relative w-full laptop:h-full grid laptop:grid-rows-1 laptop:grid-cols-[1.15fr_0.85fr] grid-rows-[auto_auto]`}
    >
      {/* Left — aspirational pitch */}
      <div
        className={`relative px-6 laptop:px-14 order-2 laptop:order-1 flex flex-col laptop:min-h-0 laptop:overflow-y-auto ${
          compactHeight ? "py-3 laptop:py-3 laptop:overflow-y-auto" : "py-5 laptop:py-6 laptop:overflow-hidden"
        }`}
      >
        <div>
          <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888] mb-1.5">
            Step 4 · Your Marketplace
          </p>
          <h2 className="text-[22px] laptop:text-[30px] font-extrabold text-[#111111] leading-[1.05]" style={{ letterSpacing: -0.5 }}>
            Your properties. Your offerings.
            <br />
            <span className="text-[#EF4949]">Your margins.</span>
          </h2>
          <p className="mt-3 text-[13px] laptop:text-[14px] text-[#5F6368] max-w-[520px] leading-relaxed">
            Add anything guests will love — rentals, experiences, welcome
            packages, add-ons. You keep 100% of what you charge on top of the
            platform fee.
          </p>
        </div>

        {/* Hero example + mini examples */}
        <div
          className={`flex-1 min-h-0 grid grid-rows-[minmax(0,1.8fr)_auto] laptop:grid-rows-[minmax(0,2fr)_auto] gap-3 ${
            compactHeight ? "mt-3 laptop:mt-4" : "mt-4 laptop:mt-6"
          }`}
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden rounded-[20px] border border-[#EAEAEA] shadow-[0_18px_60px_rgba(0,0,0,0.08)]"
          >
            <img
              src={EXAMPLE_OFFERINGS[0].img}
              alt={EXAMPLE_OFFERINGS[0].title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Top-left "Your brand" pill — always visible */}
            <div className="absolute top-3 left-3 z-10">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 backdrop-blur text-[9px] font-bold uppercase tracking-[0.18em] text-white border border-white/25">
                Your brand
              </span>
            </div>

            {/* Stronger bottom gradient so title + subtitle always read */}
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.1) 65%, transparent 100%)",
              }}
            />
            <div
              className={`relative h-full flex flex-col justify-end ${
                compactHeight
                  ? "min-h-[180px] laptop:min-h-[220px] desktop:min-h-[260px] p-3.5 laptop:p-4"
                  : "min-h-[220px] laptop:min-h-[280px] desktop:min-h-[340px] p-4 laptop:p-5"
              }`}
            >
              <span className="block text-[9px] laptop:text-[10px] font-bold uppercase tracking-[0.18em] text-white/85 mb-1">
                {EXAMPLE_OFFERINGS[0].tag}
              </span>
              <div className="flex items-end justify-between gap-3">
                <div className="min-w-0">
                  <h3
                    className="text-[17px] laptop:text-[20px] font-extrabold text-white leading-tight"
                    style={{ letterSpacing: -0.3, textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}
                  >
                    {EXAMPLE_OFFERINGS[0].title}
                  </h3>
                  <p className="text-[11px] laptop:text-[12px] text-white/85 mt-1 leading-snug">
                    An example of what your marketplace can offer.
                  </p>
                </div>
                <span
                  className="text-[18px] laptop:text-[22px] font-extrabold text-white tabular-nums flex-shrink-0"
                  style={{ letterSpacing: -0.3, textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}
                >
                  {EXAMPLE_OFFERINGS[0].price}
                </span>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-2 gap-3">
            {EXAMPLE_OFFERINGS.slice(1).map((o, i) => (
              <motion.div
                key={o.title}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05 + i * 0.05 }}
                className="relative overflow-hidden rounded-[14px] border border-[#EAEAEA]"
                style={{ aspectRatio: "16 / 10" }}
              >
                <img src={o.img} alt={o.title} className="absolute inset-0 w-full h-full object-cover" />
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.1) 55%, transparent 100%)",
                  }}
                />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-white/80">{o.tag}</span>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[12px] laptop:text-[13px] font-bold text-white truncate">{o.title}</span>
                    <span className="text-[11px] font-bold text-white/90 tabular-nums flex-shrink-0">{o.price}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className={`flex items-center gap-3 ${compactHeight ? "mt-3" : "mt-4"}`}>
          <button
            onClick={onCreate}
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-[12px] bg-[#EF4949] text-white text-[14px] font-bold hover:bg-[#DC2626] transition-colors shadow-[0_8px_32px_rgba(239,73,73,0.14)]"
          >
            <Plus size={15} strokeWidth={2.5} />
            Add your first offering
          </button>
          {customServices.length > 0 && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#10B981]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
              {customServices.length} draft{customServices.length === 1 ? "" : "s"} ready to go live
            </span>
          )}
        </div>
      </div>

      {/* Right — phone */}
      <div className="relative order-1 laptop:order-2 overflow-hidden laptop:min-h-0 h-[360px] tabletOnly:h-[440px] laptop:h-auto">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(500px 400px at 50% 40%, rgba(239,73,73,0.08), transparent 60%)",
          }}
        />
        <div
          className={`relative h-full flex items-center justify-center ${
            compactHeight ? "py-1 laptop:py-4" : "py-2 laptop:py-6"
          }`}
        >
          <PhoneAnchor
            stage={4}
            enabledServices={enabledServices}
            hostLogo={hostLogo}
            compactHeight={compactHeight}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="hidden laptop:flex absolute top-6 right-6 items-center gap-2 px-3 py-2 rounded-full bg-white border border-[#EAEAEA] shadow-[0_10px_30px_rgba(0,0,0,0.06)]"
        >
          <Sparkles size={12} className="text-[#EF4949]" />
          <span className="text-[10px] font-semibold text-[#111111]">Your branded guest storefront</span>
          <ArrowUpRight size={11} className="text-[#888888]" />
        </motion.div>
      </div>
    </div>
  );
}
